import { Alert, Box, Button, Typography } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { useEffect, useState } from 'react';
import {
  ArrowTab,
  CustomTabPanel,
  IconLibrary,
  StackablePageButtons,
  PageSection,
  ArrowTabGroup,
} from '@product-framework/index';
import {
  SkeletonListBoxSelector,
  SnackbarApiErrorMessage,
  useDataProvider,
  useEntityPermission,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import IndustriesServiced from './components/IndustriesServiced';
import ProductLinesSelector from './components/ProductLinesSelector';
import IndustriesServicedSelector from './components/IndustriesServicedSelector';
import BrandsSelector from './components/BrandsSelector';

function AutoSaveMessage({ permission = 'WRITE' }) {
  if (permission !== 'WRITE') {
    return null;
  }
  return (
    <Grid2 display="flex" justifyContent="center">
      <Box width="255px">
        <Alert severity="info">Changes saved automatically</Alert>
      </Box>
    </Grid2>
  );
}

function Skills({ serviceProviderId, onChange = () => {} }) {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();

  const { getPermission } = useEntityPermission();

  const [tabValue, setTabValue] = useState(0);
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl } = useDataProvider(provider);
  const [stackablePageButtons, setStackablePageButtons] = useState([]);
  const [view, setView] = useState(0);
  const [hasIndustriesServiced, setHasIndustriesServiced] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState({});
  const [permissions, setPermissions] = useState({});

  const configureIndustriesButton = {
    label: formatMessage({
      id: 'SERVICE_PROVIDER_SKILLS_CONFIGURE_INDUSTRIES_SERVICED_BUTTON',
      defaultMessage: 'Configure Industries Serviced',
    }),
    Icon: IconLibrary.Industries,
    variant: 'outlined',
    onClick: () => {
      setView(0);
    },
  };

  const configureProductLineAndBrandsButton = {
    label: formatMessage({
      id: 'SERVICE_PROVIDER_SKILLS_CONFIGURE_PRODUCT_LINES_AND_BRANDS_BUTTON',
      defaultMessage: 'Configure Product Lines and Brands',
    }),
    Icon: IconLibrary.ServiceAdministratorProductLines,
    variant: 'outlined',
    onClick: () => {
      setView(1);
    },
  };

  const fetchCurrentIndustriesServiced = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/service-industries`,
    );
    if (response.ok) {
      return response?.data?.data || [];
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const initialize = async () => {
    const permissionsFound = await getPermission({
      name: 'SERVICEPROVIDERSKILLS',
    });
    setPermissions(permissionsFound);
    const result = await fetchCurrentIndustriesServiced();
    if (result && result.length > 0) {
      setHasIndustriesServiced(result && result.length > 0);
      setView(1);
    } else {
      setView(0);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    if (view === 0) {
      setStackablePageButtons([configureProductLineAndBrandsButton]);
    } else if (view === 1) {
      setStackablePageButtons([configureIndustriesButton]);
    }
  }, [view]);

  if (view === null) {
    return <SkeletonListBoxSelector height="400px" />;
  }

  return (
    <PageSection
      HeaderContent={
        <StackablePageButtons
          backButton={false}
          buttons={stackablePageButtons}
        />
      }
      label={formatMessage({
        id: 'SERVICE_PROVIDER_SKILLS_EDIT_HEADING',
        defaultMessage: 'Service Provider Skills',
      })}>
      {view === 0 && (
        <Grid2 mb={5} mt={5} textAlign="center">
          <IndustriesServicedSelector
            permissions={permissions}
            setHasIndustriesServiced={setHasIndustriesServiced}
            onChange={data => {
              if (data && data.length > 0) {
                setHasIndustriesServiced(true);
              }
              onChange();
            }}
            serviceProviderId={serviceProviderId}
          />
          <AutoSaveMessage permission={permissions.industryId} />
        </Grid2>
      )}
      {view === 1 && (
        <Box>
          {!hasIndustriesServiced ? (
            <Grid2 mb={5} mt={5} textAlign="center">
              <Box
                sx={{
                  width: '40%',
                  margin: '0 auto',
                  height: '300px',
                }}>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 200 }}>
                  {formatMessage({
                    id: 'SERVICE_PROVIDER_SKILLS_NO_INDUSTRIES_SERVICED_HEADING',
                    defaultMessage:
                      'You have not added any Industries Serviced yet.',
                  })}
                </Typography>
                <Button
                  onClick={() => setView(0)}
                  startIcon={<IconLibrary.Industries />}
                  variant="contained">
                  Add Industries Serviced
                </Button>
              </Box>
            </Grid2>
          ) : (
            <Grid2 container spacing={2}>
              <Grid2 xs={12} md={3}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500 }}
                  gutterBottom>
                  {formatMessage({
                    id: 'SERVICE_PROVIDER_SKILLS_INDUSTRIES_SERVICED_HEADING',
                    defaultMessage: 'Industries Serviced',
                  })}
                </Typography>
                <IndustriesServiced
                  onChange={data => setSelectedIndustry(data)}
                  serviceProviderId={serviceProviderId}
                />
              </Grid2>
              <Grid2 xs={12} md={9}>
                <Box>
                  <ArrowTabGroup
                    value={tabValue}
                    onChange={(event, newValue) => setTabValue(newValue)}>
                    <ArrowTab
                      arrowHeadArea={89}
                      maxTabs={2}
                      label="Product Lines"
                    />
                    <ArrowTab arrowBackArea={5} maxTabs={2} label="Brands" />
                  </ArrowTabGroup>
                </Box>
                <Grid2 xs={12}>
                  <CustomTabPanel value={tabValue} index={0}>
                    <ProductLinesSelector
                      permissions={permissions}
                      selectedIndustry={selectedIndustry}
                      serviceProviderId={serviceProviderId}
                    />
                  </CustomTabPanel>
                  <CustomTabPanel value={tabValue} index={1}>
                    <BrandsSelector
                      permissions={permissions}
                      selectedIndustry={selectedIndustry}
                      serviceProviderId={serviceProviderId}
                    />
                  </CustomTabPanel>
                </Grid2>
                <AutoSaveMessage permission={permissions.industryId} />
              </Grid2>
            </Grid2>
          )}
        </Box>
      )}
    </PageSection>
  );
}
export default Skills;
